import {isTextAnnotation, resolveAnchor} from './annotationAnchors';
import type {TextProjection} from './annotationTextProjection';
import {normalizedRangeToDomRange} from './annotationTextProjection';
import type {Annotation, TextAnnotation} from './annotationTypes';

/** Line-box centers this close share a line; mixed font sizes jitter them apart. */
const LINE_TOLERANCE_PX = 8;

export interface AnnotationNoteLine {
  /** The line's vertical center within the scroller's scrolled content. */
  center: number;
  /** The noted marks on this line, in document order. */
  annotations: Annotation[];
}

export interface MeasuredNote {
  annotation: TextAnnotation;
  center: number;
}

/**
 * One entry per visual line, so several notes on a line yield a single
 * indicator. Each line keeps the input's order and takes its first note's center.
 */
export function groupNotesByLine(notes: MeasuredNote[]): AnnotationNoteLine[] {
  const lines: AnnotationNoteLine[] = [];
  notes.forEach(({annotation, center}) => {
    const line = lines.find(
      (candidate) => Math.abs(candidate.center - center) <= LINE_TOLERANCE_PX
    );
    if (line) {
      line.annotations.push(annotation);
      return;
    }
    lines.push({center, annotations: [annotation]});
  });
  return lines;
}

/**
 * Where each noted mark's first line box sits in the scroller's scrolled
 * content. A mark whose anchor no longer resolves is dropped, as the paint drops it.
 */
export function measureNoteLines(
  annotations: Annotation[],
  projection: TextProjection,
  scroller: HTMLElement
): AnnotationNoteLine[] {
  const scrollerRect = scroller.getBoundingClientRect();
  // Viewport y to scrolled-content y: past the border, then past what's scrolled off.
  const contentOffset = scroller.scrollTop - scrollerRect.top - scroller.clientTop;
  // Whole-supplement marks have no line box; they surface on the supplement itself.
  const textMarks = annotations.filter(isTextAnnotation);
  // The store keeps marks in the order they were written; a line reads in its own.
  const inDocumentOrder = textMarks.sort((a, b) => a.anchor.start - b.anchor.start);
  const measured = inDocumentOrder.flatMap((annotation) => {
    const resolved = resolveAnchor(annotation.anchor, projection);
    const range = resolved && normalizedRangeToDomRange(projection, resolved);
    const firstLine = range?.getClientRects()[0];
    if (!firstLine) {
      return [];
    }
    return [{annotation, center: firstLine.top + firstLine.height / 2 + contentOffset}];
  });
  return groupNotesByLine(measured);
}
